import { useCallback, useRef, useState } from 'react';
import { useCreateProject, type CreatedProject } from '../data/useProjects';
import { safeAnalytics } from '../../utils/analytics';
import { ANALYTICS_EVENTS } from '../../constants/analytics';
import { ApiResponseError } from '../../services/api/http-client.types';
import {
  destinationProblem,
  destinationProjectId,
  type CreateDestination,
} from '../../components/features/create/destination/createDestination';

/** Why a create was refused, in the terms the form shows. */
export type CollectionCreateRefusal =
  | { readonly kind: 'taken'; readonly existingId?: string }
  | { readonly kind: 'trash' }
  | { readonly kind: 'parent'; readonly problem: 'gone' | 'forbidden' }
  | { readonly kind: 'other'; readonly message: string };

function refusalOf(error: unknown): CollectionCreateRefusal {
  const problem = destinationProblem(error);
  if (problem) return { kind: 'parent', problem };
  if (error instanceof ApiResponseError && error.code === 'COLLECTION_NAME_TAKEN') {
    const existingId = error.details?.existingId;
    return { kind: 'taken', existingId: typeof existingId === 'string' ? existingId : undefined };
  }
  if (error instanceof ApiResponseError && error.code === 'COLLECTION_NAME_IN_TRASH') {
    return { kind: 'trash' };
  }
  return {
    kind: 'other',
    message: error instanceof Error ? error.message : 'Could not create the collection.',
  };
}

interface CollectionCreateInput {
  readonly name: string;
  readonly isPrivate: boolean;
}

/**
 * Create a collection under `parent` from the create modal: case-insensitive
 * duplicate check on, "Private to me" in the same request, analytics on
 * success, and every refusal mapped to something the form can say. A second
 * submit while one is in flight is ignored.
 */
export function useCollectionCreate(parent: CreateDestination, inline: boolean) {
  const { mutateAsync: createProject } = useCreateProject();
  const inFlight = useRef(false);
  const [refusal, setRefusal] = useState<CollectionCreateRefusal | null>(null);
  // Set the moment a submit starts and cleared when it settles: the one
  // pending signal (the mutation's isPending only flips a render later).
  const [submitting, setSubmitting] = useState(false);

  const submit = useCallback(
    async ({ name, isPrivate }: CollectionCreateInput): Promise<CreatedProject | null> => {
      if (inFlight.current) return null;
      inFlight.current = true;
      setSubmitting(true);
      setRefusal(null);
      const parentProjectId = destinationProjectId(parent);
      try {
        const created = await createProject({
          name: name.trim(),
          parentProjectId,
          rejectSimilarName: true,
          ...(isPrivate && { isPrivate: true }),
        });
        safeAnalytics.setPersonProperty('last_collection_created_at', new Date().toISOString());
        safeAnalytics.track(ANALYTICS_EVENTS.COLLECTION_CREATED, {
          entity_type: 'collection',
          creation_method: 'text',
          creation_screen: 'create_modal',
          creation_trigger: 'create_modal',
          parent_entity_type: parentProjectId ? 'collection' : undefined,
          parent_entity_id: parentProjectId,
          is_sub_project: !!parentProjectId,
          is_private: isPrivate,
          privacy_mode: created.privacyMode,
          inline,
          timestamp: new Date().toISOString(),
        });
        safeAnalytics.track(ANALYTICS_EVENTS.CONTENT_CREATED, {
          content_type: 'collection',
          creation_method: 'text',
        });
        return created;
      } catch (error: unknown) {
        setRefusal(refusalOf(error));
        return null;
      } finally {
        inFlight.current = false;
        setSubmitting(false);
      }
    },
    [createProject, parent, inline],
  );

  return {
    submit,
    isPending: submitting,
    refusal,
    clearRefusal: () => setRefusal(null),
  };
}
